import { useCallback } from "react";
import { IJumpMarkItem, IJumpMarksState } from "src/components-mui/common/jump-marks/jump-marks.types";
import { getJumpMarkUrl, isPageTopItem } from "src/components-mui/common/jump-marks/jump-marks.utils";
import { focusWithoutScrolling } from "src/utils/dom/focus/focus";
import { getSmoothScrollBehavior } from "src/utils/dom/reduced-motion/reduced-motion";
import { waitForWindowScrollEnd } from "src/utils/dom/scroll-end/scroll-end";

interface IUseSectionNavigationParams {
    items: readonly IJumpMarkItem[];
    state: IJumpMarksState;
    anchors: ReadonlyMap<string, HTMLElement>;
}

interface IScrollToTargetParams {
    target: HTMLElement | undefined;
    behavior: ScrollBehavior;
}

export const scrollToTarget = ({ target, behavior }: IScrollToTargetParams) => {
    if (target) {
        target.scrollIntoView({ behavior, block: "start" });
        return;
    }

    window.scrollTo({ top: 0, behavior });
};

const replaceUrlWithoutRouteChange = (url: string) => {
    window.history.replaceState(window.history.state, "", url);
};

export const useSectionNavigation = ({ items, state, anchors }: IUseSectionNavigationParams) => {
    const scrollToSectionAndGetArrivedElement = useCallback(
        async (id: string) => {
            const anchor = anchors.get(id);
            const isPageTop = isPageTopItem({ items, id });

            if (!anchor && !isPageTop) {
                return undefined;
            }

            const target = isPageTop ? undefined : anchor;
            const scrollEnd = waitForWindowScrollEnd();
            state.jumpTargetId = id;
            scrollToTarget({ target, behavior: getSmoothScrollBehavior() });

            const hasScrollEnded = await scrollEnd;

            const wasOvertakenByAnotherJump = state.jumpTargetId !== id;

            if (wasOvertakenByAnotherJump) {
                return undefined;
            }

            // Re-aligning before the smooth scroll ended would cut it short.
            if (hasScrollEnded) {
                scrollToTarget({ target, behavior: "auto" });
            }

            state.visibleId = id;
            state.jumpTargetId = null;

            return anchor;
        },
        [anchors, items, state]
    );

    const scrollToSection = useCallback(
        (id: string) => {
            scrollToSectionAndGetArrivedElement(id);
        },
        [scrollToSectionAndGetArrivedElement]
    );

    const navigateToSection = useCallback(
        async (id: string) => {
            replaceUrlWithoutRouteChange(getJumpMarkUrl({ items, id, location: window.location }));

            const arrivedElement = await scrollToSectionAndGetArrivedElement(id);

            if (arrivedElement) {
                focusWithoutScrolling(arrivedElement);
            }
        },
        [items, scrollToSectionAndGetArrivedElement]
    );

    return { scrollToSection, navigateToSection };
};
